import { Button } from "primereact/button";
import { useEffect, useRef, useState, useCallback } from "react";

import { CanvasContainer, Canvas, EmptyStateText } from "../../styled";

type Props = {
  width?: number; // CSS pixels (before DPR scaling)
  height?: number;
  lineWidth?: number; // base stroke width (auto-scaled by DPR)
  disabled?: boolean;
  onChange?: (hasDrawing: boolean) => void;
  onSignatureChange?: (signature: string | null) => void;
};

export function SignaturePad({
  width = 600,
  height = 200,
  lineWidth = 2,
  disabled = false,
  onChange,
  onSignatureChange,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Initialize canvas with proper DPR scaling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    // Set canvas resolution (actual pixels)
    canvas.width = width * dpr;
    canvas.height = height * dpr;

    // Set canvas display size (CSS pixels)
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // Scale context to match DPR
    ctx.scale(dpr, dpr);

    // Set drawing properties
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = lineWidth;
  }, [width, height, lineWidth]);

  const getCoordinates = useCallback(
    (event: MouseEvent | TouchEvent): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();
      let clientX: number;
      let clientY: number;

      if ("touches" in event) {
        if (event.touches.length === 0) return null;
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    },
    []
  );

  const drawLine = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
    },
    []
  );

  const handleStart = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (disabled) return;

      event.preventDefault();
      const coords = getCoordinates(event);
      if (!coords) return;

      setIsDrawing(true);
      lastPointRef.current = coords;

      // Draw a dot for single tap/click
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.beginPath();
      ctx.arc(coords.x, coords.y, lineWidth / 2, 0, 2 * Math.PI);
      ctx.fill();
    },
    [disabled, getCoordinates, lineWidth]
  );

  const handleMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isDrawing || disabled) return;

      event.preventDefault();
      const coords = getCoordinates(event);
      if (!coords || !lastPointRef.current) return;

      drawLine(lastPointRef.current, coords);
      lastPointRef.current = coords;

      if (!hasDrawn) {
        setHasDrawn(true);
        onChange?.(true);
      }
    },
    [isDrawing, disabled, getCoordinates, drawLine, hasDrawn, onChange]
  );

  const handleEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isDrawing) return;

      event.preventDefault();
      setIsDrawing(false);
      lastPointRef.current = null;

      // Export signature as data URL
      const canvas = canvasRef.current;
      if (canvas && hasDrawn) {
        const signatureData = canvas.toDataURL("image/png");
        onSignatureChange?.(signatureData);
      }
    },
    [isDrawing, hasDrawn, onSignatureChange]
  );

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    setIsDrawing(false);
    lastPointRef.current = null;
    onChange?.(false);
    onSignatureChange?.(null);
  }, [onChange, onSignatureChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    // Mouse events
    canvas.addEventListener("mousedown", handleStart as any);
    canvas.addEventListener("mousemove", handleMove as any);
    canvas.addEventListener("mouseup", handleEnd as any);
    canvas.addEventListener("mouseleave", handleEnd as any);

    // Touch events
    canvas.addEventListener("touchstart", handleStart as any);
    canvas.addEventListener("touchmove", handleMove as any);
    canvas.addEventListener("touchend", handleEnd as any);
    canvas.addEventListener("touchcancel", handleEnd as any);

    return () => {
      canvas.removeEventListener("mousedown", handleStart as any);
      canvas.removeEventListener("mousemove", handleMove as any);
      canvas.removeEventListener("mouseup", handleEnd as any);
      canvas.removeEventListener("mouseleave", handleEnd as any);
      canvas.removeEventListener("touchstart", handleStart as any);
      canvas.removeEventListener("touchmove", handleMove as any);
      canvas.removeEventListener("touchend", handleEnd as any);
      canvas.removeEventListener("touchcancel", handleEnd as any);
    };
  }, [handleStart, handleMove, handleEnd]);

  return (
    <div className="flex flex-column gap-2 w-full mt-2">
      <p className="text-sm font-semibold gray-text mb-2">
        Signature Required:
      </p>
      <CanvasContainer $disabled={disabled}>
        <Canvas ref={canvasRef} $disabled={disabled} />
        {!hasDrawn && (
          <EmptyStateText>Sign here with your finger or mouse</EmptyStateText>
        )}
      </CanvasContainer>
      <p className="text-xs light-gray ">
        By signing above, you acknowledge that you have received and understand
        this notice.
      </p>
      <Button
        type="button"
        onClick={clearSignature}
        disabled={!hasDrawn || disabled}
        className="p-button-outlined p-button-danger w-fit align-self-end p-button-sm"
      >
        Clear Signature
      </Button>
    </div>
  );
}
